import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BaseWidgetComponent } from '../base-widget/base-widget.component';
import { WidgetService } from 'src/app/services/widget.service';
import { Job, JobsService } from 'src/app/services/jobs.service';
import { ConfigService } from 'src/app/services/config.service';
import { TeamsService } from 'src/app/services/teams.service';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { DialogService } from '../../../services/dialog.service';

@Component({
    selector: 'app-jobs-widget',
    templateUrl: './jobs-widget.component.html',
    styleUrls: ['../base-widget/base-widget.component.scss', './jobs-widget.component.scss'],
    standalone: false
})
export class JobsWidgetComponent extends BaseWidgetComponent implements OnInit, AfterViewInit {
  private dialogs = inject(DialogService);

  @Output() onEditFile = new EventEmitter<{ path: any }>();

  // Callback alternative for when EventEmitter outputs aren't available (e.g., ngComponentOutlet)
  @Input() editFileCallback: ((path: string) => void) | null = null;

  public path: string;
  public fileType: string;
  public title: string = null;
  public selectedDataset: string = null;
  public filterText = null;
  public dataSource = new MatTableDataSource<Job>([]);
  public displayedColumns: string[] = ['name', 'schedule', 'type', 'actions'];
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort, { static: false }) sort: MatSort;
  @ViewChild('jobsTable') jobsTable!: ElementRef;
  public pageSize = 10;

  // Cron editor state
  public editingJob: Job | null = null;
  public isSavingSchedule: boolean = false;

  public right = 80;
  public left = 20;
  public isLoading: boolean = true;

  constructor(
    private _widgetService: WidgetService,
    private _jobs: JobsService,
    private _elRef: ElementRef,
    private _http: HttpClient,
    private _teamsService: TeamsService
  ) {
    super();
    (<any>window).jobs = this;
  }

  ngOnInit(): void {
    this._subs.add = this._widgetService.resize$.subscribe((response) => {
      setTimeout(() => {
        try {
          this.optimizePageSize();
        } catch {}
      }, 100);
    });

    this.load();
  }

  private load() {
    this.isLoading = true;
    this._jobs.load(this.teamName).subscribe((response) => {
      this.dataSource.data = <any[]>response;
      this.optimizePageSize();
      this.isLoading = false;
    });
  }

  public reloadData() {
    this.isLoading = true;
    this.load();
  }

  public ngAfterViewInit(): void {
    this.applyTableSettings();

  }

  applyTableSettings() {
    setTimeout(() => {
      this.dataSource.sort = this.sort;
      this.dataSource.paginator = this.paginator;
      this.optimizePageSize();
    }, 0);
  }

  public optimizePageSize(): void {
    let offsetHeight = this._elRef.nativeElement.offsetHeight;

    const oldPageSize = this.pageSize;
    const calcuatedPageSize = Math.floor(0.0125 * offsetHeight);
    if (calcuatedPageSize < 5) {
      this.pageSize = 5;
    } else {
      this.pageSize = calcuatedPageSize;
    }

    if (oldPageSize !== this.pageSize) {
      setTimeout(() => this.paginator._changePageSize(this.pageSize), 100);
    }
  }


  onViewData(input: any) {
    this.path = input.path;
    this.fileType = input.fileType;
    this.title = input.path.split('/').pop();
    this.widgetView = 'DETAIL';
    this.right = 100;
    this.left = 0;
  }

  onSelect(item: any) {
    const path = item.path;
    if (this.editFileCallback) {
      this.editFileCallback(path);
    } else {
      this.onEditFile.emit({ path });
    }
  }


  applyFilter(filterValue: string) {
    this.dataSource.filter = filterValue.trim().toLowerCase();
    if (this.dataSource.paginator) this.dataSource.paginator.firstPage();
  }


  close() {
    if (this.widgetView === 'DETAIL') {
      this.path = null;
      this.fileType = null;
      this.title = null;
      this.widgetView = 'LIST';
      this.right = 80;
      this.left = 20;

    } else {
      super.close();
    }
  }

  async deleteJob(event: Event, job: Job): Promise<void> {
    event.stopPropagation();
    if (!await this.dialogs.confirm(`Delete job "${job.name}"?`)) return;

    this.isLoading = true;
    this._jobs.delete(this.teamName, job.path).subscribe({
      next: () => this.load(),
      error: (err) => {
        this.isLoading = false;
        console.error('Failed to delete job:', err);
      }
    });
  }

  openScheduleEditor(event: Event, job: Job): void {
    event.stopPropagation();
    this.editingJob = job;
  }

  closeScheduleEditor(): void {
    this.editingJob = null;
  }

  saveSchedule(schedule: string): void {
    if (!this.editingJob) return;

    const job = this.editingJob;
    this.isSavingSchedule = true;

    this._jobs.setSchedule(this.teamName, job.path, schedule).subscribe({
      next: (response) => {
        this.isSavingSchedule = false;
        if (response.success) {
          // Update local data
          job.schedule = schedule;
          this.editingJob = null;
        } else {
          console.error('Failed to save schedule:', response.error);
        }
      },
      error: (err) => {
        this.isSavingSchedule = false;
        console.error('Error saving schedule:', err);
      }
    });
  }

  getScheduleDisplay(schedule: string): string {
    if (!schedule) return 'Not scheduled';
    return schedule;
  }

  async createJob(): Promise<void> {
    const name = await this.dialogs.prompt('New job filename (e.g., my_job.py):');
    if (!name || !name.trim()) return;

    const filename = name.trim();
    const teamConfig = await this._teamsService.getConfigAsync(this.teamName);
    const jobPath = `${teamConfig.path.jobs}/${filename}`;

    // Default Python job template
    const template = filename.endsWith('.py')
      ? `#!/usr/bin/env python3
"""
Job: ${filename}
"""

def main():
    pass

if __name__ == "__main__":
    main()
`
      : '';

    this.isLoading = true;
    this._http.post<any>(`${ConfigService.muxUrl}/commander/write_file`, {
      path: jobPath,
      content: template
    }).subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response.success) {
          // Reload the jobs list
          this.load();
          // Open the new file in editor
          if (this.editFileCallback) {
            this.editFileCallback(jobPath);
          } else {
            this.onEditFile.emit({ path: jobPath });
          }
        } else {
          console.error('Failed to create job:', response.error);
          this.dialogs.alert('Failed to create job: ' + (response.error || 'Unknown error'));
        }
      },
      error: (err) => {
        this.isLoading = false;
        console.error('Failed to create job:', err);
        this.dialogs.alert('Failed to create job');
      }
    });
  }
}
